var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { WebGLRenderer, Scene, Vector3, AmbientLight, Clock, Color, Spherical, BufferGeometry, BoxHelper, Geometry, } from "three";
import { getStateBounds, BuildMeshMap, ComputeFocalPoints, pointNormalFromUV, buildBoundingBoxGeometry, documentXYtoUV, releaseCanvases, } from "../common";
import { MAX_GEOMETRY_DIMENSION } from "../config";
import { OrbitalCamera } from "../cameras";
import { AnimationDirection, AnimationManager, CenterAnimation } from "../animations";
import { updateCanvasShadows } from "../rendering/shadows";
import { MaterialManager } from "../rendering/materialManager";
import { EventManager } from "../events";
import { SkyBox } from "../rendering/skyBox";
export class Preview {
    constructor(config) {
        var _a, _b, _c;
        // Custom product to display
        Object.defineProperty(this, "vortexProduct", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "productLoadingOptions", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        // Environment product will be shown in
        Object.defineProperty(this, "environment", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        // Handling material generation and life cycle
        Object.defineProperty(this, "materialManager", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        // WebGL Rendering
        Object.defineProperty(this, "webGlOptions", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "renderer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "rafId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        // HTML canvas element and resizing
        Object.defineProperty(this, "canvas", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "canvasResizeObserver", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        // Vortex canvas interactions
        Object.defineProperty(this, "eventManager", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "productRequiresUpdate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "canvasRequiresUpdate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "holdRenderForUpdate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "scene", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "camera", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "cameraOptions", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "areaLights", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "skyBox", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "currentGeometry", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "maximumGeometrySize", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stateBounds", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {}
        });
        Object.defineProperty(this, "meshMap", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "normalizationFactor", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 1
        });
        Object.defineProperty(this, "clock", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Clock()
        });
        Object.defineProperty(this, "animationManager", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new AnimationManager()
        });
        Object.defineProperty(this, "boundingBox", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "boxHelper", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "disposed", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "resizeObserverRafId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "renderLoop", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                const delta = this.clock.getDelta();
                this.renderFrame(delta);
                // Call back to render the next frame
                this.rafId = requestAnimationFrame(this.renderLoop);
            }
        });
        this.environment = config.environment;
        this.cameraOptions = config.cameraOptions;
        this.canvas = config.canvas;
        const canvasRect = config.canvas.getBoundingClientRect();
        this.materialManager = new MaterialManager(config);
        // Build and connect the webGL renderer to the supplied canvas
        this.webGlOptions = (_a = config.webGlOptions) !== null && _a !== void 0 ? _a : {
            preserveDrawingBuffer: false,
        };
        const { preserveDrawingBuffer } = this.webGlOptions;
        this.renderer = new WebGLRenderer({
            antialias: true,
            alpha: true,
            canvas: this.canvas,
            preserveDrawingBuffer,
            // logarithmicDepthBuffer is needed to fix a "z-fighting" issue om some products.
            // https://gitlab.com/Cimpress-Technology/DocDesign/Rendering/vortex/-/merge_requests/461
            logarithmicDepthBuffer: true,
        });
        // Ensure the pixel ratio is at least 2 for better details on edges
        this.renderer.setPixelRatio(Math.max(2, devicePixelRatio));
        this.renderer.setClearColor(0, 0);
        // Build the orbital camera with the canvas dimensions
        this.camera = new OrbitalCamera(this.cameraOptions, {
            aspectRatio: Math.max(canvasRect.width, 1) / Math.max(canvasRect.height, 1),
            domElement: this.canvas,
        });
        // Container for the entire preview
        this.scene = new Scene();
        // Add event manager for canvas interactions
        this.eventManager = new EventManager(this.canvas, this, this.scene, this.camera);
        // Add the ambient lighting to the scene
        const ambientIntensity = (_c = (_b = this.environment.lightingOptions) === null || _b === void 0 ? void 0 : _b.ambientIntensity) !== null && _c !== void 0 ? _c : 1.0;
        this.scene.add(new AmbientLight(0x111111, 11.1504 * ambientIntensity));
        // Observes when the provided canvas size changes and properly updates the camera and rendering client
        this.canvasResizeObserver = new ResizeObserver((entries) => {
            if (entries.length === 0) {
                return;
            }
            // Get the current width and height of the updated canvas
            const { width, height } = entries[0].contentRect;
            // Return if width or height is 0. This can happen on `display: none` style of any container element
            if (width === 0 || height === 0) {
                return;
            }
            this.resizeObserverRafId = requestAnimationFrame(() => {
                // Update the renderer with the new width and height
                this.camera.aspectRatio = width / height;
                this.renderer.setSize(width, height, false);
                // If a product is loaded with geometry ensure the orbit radius is updated when the canvas size changes
                if (this.currentGeometry && this.maximumGeometrySize) {
                    this.camera.resizeCamera(this.maximumGeometrySize);
                    this.canvasRequiresUpdate = true;
                }
            });
        });
    }
    get product() {
        return this.vortexProduct;
    }
    get getProductGeometry() {
        return this.currentGeometry;
    }
    // Start render loop
    start() {
        const initialized = !!this.vortexProduct;
        // Start the render loop if this is the first initialization
        if (initialized && !this.rafId) {
            this.renderLoop();
        }
        else if (!initialized) {
            throw new Error("Vortex it not initialized");
        }
    }
    /**
     * Load a new vortex product
     */
    load(vortexProduct, documentAdapter, productLoadingOptions) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            this.holdRenderForUpdate = true;
            const initialized = !!this.vortexProduct;
            if (initialized) {
                // eslint-disable-next-line no-unused-expressions
                (_a = this.vortexProduct) === null || _a === void 0 ? void 0 : _a.dispose();
                this.materialManager.dispose();
                // remove product area lights from scene if it exists
                if (this.areaLights) {
                    this.scene.remove(...this.areaLights);
                }
                // remove geometry from scene if it exists and dispose
                if (this.currentGeometry) {
                    this.scene.remove(this.currentGeometry);
                }
                // remove skybox
                if (this.skyBox) {
                    this.scene.remove(this.skyBox);
                }
                this.productRequiresUpdate = true;
            }
            this.vortexProduct = vortexProduct;
            this.productLoadingOptions = productLoadingOptions;
            // Load the product's geometry and add it to the scene
            this.currentGeometry = yield this.loadGeometry(this.vortexProduct);
            this.scene.add(this.currentGeometry);
            // render bounding box of geometry for debugging or model config setup purposes
            const boundingBox = vortexProduct.getCurrentState().boundingBox;
            if (boundingBox) {
                this.boundingBox = buildBoundingBoxGeometry(boundingBox, this.normalizationFactor);
            }
            if (this.webGlOptions.renderBoundingBox) {
                this.boxHelper = new BoxHelper((_b = this.boundingBox) !== null && _b !== void 0 ? _b : this.currentGeometry, new Color(0x000000));
                this.scene.add(this.boxHelper);
            }
            // Initialize state bounds after re-normalizing geometry
            this.scene.updateMatrixWorld();
            this.stateBounds = yield getStateBounds(this, vortexProduct, this.currentGeometry, this.normalizationFactor);
            // Add the product's lighting to the scene
            const pointLightIntensity = (_e = (_d = (_c = this.environment) === null || _c === void 0 ? void 0 : _c.lightingOptions) === null || _d === void 0 ? void 0 : _d.pointLightIntensity) !== null && _e !== void 0 ? _e : 1.0;
            const directionalLightIntensity = (_h = (_g = (_f = this.environment) === null || _f === void 0 ? void 0 : _f.lightingOptions) === null || _g === void 0 ? void 0 : _g.directionalLightIntensity) !== null && _h !== void 0 ? _h : 1.0;
            this.areaLights = vortexProduct.getAreaLights(pointLightIntensity, directionalLightIntensity);
            this.scene.add(...this.areaLights);
            if (this.environment.skyBox) {
                const currentState = this.vortexProduct.getCurrentState();
                const currentBounds = this.stateBounds[currentState.id];
                this.skyBox = new SkyBox(this.environment.skyBox.height, 2000);
                this.skyBox.position.y = currentBounds.min.y + this.environment.skyBox.height;
                this.scene.add(this.skyBox);
            }
            this.observeResize();
            this.eventManager.setProduct(vortexProduct);
            // Generate focal points dynamically for surfaces that don't already contain them
            if (this.vortexProduct && this.meshMap) {
                yield ComputeFocalPoints(this.vortexProduct, this.meshMap, this);
            }
            yield Promise.all([
                this.materialManager.fetchMaterialTextures(vortexProduct),
                documentAdapter(this.materialManager.getDocumentSurfaces(vortexProduct)).then((results) => {
                    results.forEach((result) => {
                        this.materialManager.setDocumentCanvas(result.surface, result.canvas);
                    });
                }),
            ]);
            this.materialManager.updateTextures(vortexProduct, this.meshMap, this.skyBox);
            if (this.webGlOptions.renderWireframes) {
                this.materialManager.setWireframes(this.webGlOptions.renderWireframes);
            }
            this.holdRenderForUpdate = false;
            if (this.disposed) {
                // Preview is disposed while load happened
                // Call dispose again to clean up load result
                this.dispose();
            }
        });
    }
    /**
     * Re-load a product in the preview. Used if the geometry or textures of a vortex product have changed
     */
    update(documentAdapter) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.disposed) {
                console.warn("update called on disposed preview");
                return;
            }
            // Fetch all material resources asynchronously
            if (this.vortexProduct && this.materialManager) {
                const results = yield documentAdapter(this.materialManager.getDocumentSurfaces(this.vortexProduct));
                for (const result of results) {
                    this.materialManager.setDocumentCanvas(result.surface, result.canvas);
                }
            }
            if (this.disposed) {
                // Preview is disposed while fetchDocumentTextures
                // Call dispose again to clean up fetchDocumentTextures result
                this.dispose();
                return;
            }
            // When all resources to update the textures are available, hold the render loop while textures update
            this.holdRenderForUpdate = true;
            if (this.vortexProduct && this.meshMap) {
                this.materialManager.updateTextures(this.vortexProduct, this.meshMap, this.skyBox);
            }
            // Ensure the new geometry/material gets rendered if no other actions or camera movements take place
            this.productRequiresUpdate = true;
            this.holdRenderForUpdate = false;
        });
    }
    /**
     * Gets the current state of the camera view
     */
    getCameraView() {
        return {
            focalPoint: {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                x: this.camera.controls.target.x,
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                y: this.camera.controls.target.y,
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                z: this.camera.controls.target.z,
            },
            zoom: 1.0 / this.camera.zoomFactor,
            rotation: {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                azimuth: this.camera.controls.getAzimuthalAngle(),
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                polar: this.camera.controls.getPolarAngle(),
            },
        };
    }
    /**
     * Updates the mouse gesture to a defined action
     */
    setMouseGesture(gesture, action) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this.camera.controls.mouseGestures[gesture] = action;
    }
    /**
     * Updates the touch gesture to a defined action
     */
    setTouchGesture(gesture, action) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this.camera.controls.touchGestures[gesture] = action;
    }
    /**
     * Updates the camera's zoom
     */
    adjustZoom({ zoom, duration, easingFunction, }) {
        return this.camera.adjustZoomFactor(zoom, duration, easingFunction);
    }
    /**
     * Updates the camera's focal point
     */
    adjustFocalPoint({ point, duration, easingFunction, }) {
        return this.camera.focusOn(new Vector3(point.x, point.y, point.z), duration, easingFunction);
    }
    /**
     * Updates the camera's rotation
     */
    adjustRotation({ azimuth, polar, duration, easingFunction, }) {
        return this.camera.lookAt({
            azimuthAngle: azimuth,
            polarAngle: polar,
        }, duration, easingFunction);
    }
    /**
     * Partial Update web gl options
     * @param options webGlOption property to update
     */
    updateWebGlOptions(options) {
        var _a;
        if (!this.webGlOptions.renderBoundingBox && options.renderBoundingBox && this.currentGeometry) {
            // toggling render of bounding box on. false or undefined => true
            if (!this.boxHelper) {
                this.boxHelper = new BoxHelper((_a = this.boundingBox) !== null && _a !== void 0 ? _a : this.currentGeometry, new Color(0x000000));
            }
            // add bounding box object to scene for it to be rendered
            this.scene.add(this.boxHelper);
        }
        else if (this.webGlOptions.renderBoundingBox && options.renderBoundingBox === false) {
            // toggling render of bounding box off. true => false
            // remove render object from scene
            this.boxHelper && this.scene.remove(this.boxHelper);
        }
        this.webGlOptions = Object.assign(this.webGlOptions, options);
        if (this.webGlOptions.renderWireframes !== undefined) {
            this.materialManager.setWireframes(this.webGlOptions.renderWireframes);
        }
        this.productRequiresUpdate = true;
    }
    /**
     * Adds a colored highlight to a specific surface. The optional color is defined with css syntax, otherwise
     * the default will be yellow.
     */
    addHighlight({ surfaceId, color }) {
        // If a custom color is specified use that, otherwise default to yellow
        this.materialManager.updateColor(surfaceId, color ? new Color(color) : new Color(1, 1, 0.3));
        // Ensure the product gets the update
        this.productRequiresUpdate = true;
    }
    /**
     * Removes a highlight from a specific surface.
     */
    removeHighlight({ surfaceId }) {
        this.materialManager.restoreColor(surfaceId);
        this.productRequiresUpdate = true;
    }
    /**
     * Add an event listener for vortex interactions
     */
    addEventListener(type, listener) {
        this.eventManager.addListener(type, listener);
    }
    /**
     * Remove an event listener for vortex interactions
     */
    removeEventListener(type, listener) {
        this.eventManager.removeListener(type, listener);
    }
    /**
     * Focus the camera on a given surface. This may also trigger model animations to show occluded surfaces
     */
    focusOnSurface(_a) {
        return __awaiter(this, arguments, void 0, function* ({ surfaceId, duration, easingFunction, }) {
            if (!this.vortexProduct) {
                throw new Error("Vortex Product must be defined before a surface can be focused on!");
            }
            const { focalPoint } = this.vortexProduct.getSurfaceMapping()[surfaceId];
            if (focalPoint) {
                // Get the camera view vector and reverse it
                const { cameraView } = focalPoint;
                const cameraViewReverse = new Vector3(-cameraView.x, -cameraView.y, -cameraView.z);
                // Map the reversed normal into spherical coordinates
                const spherical = new Spherical().setFromVector3(cameraViewReverse);
                const cameraAction = this.lookAtAngle({
                    azimuth: spherical.theta,
                    polar: spherical.phi,
                    duration,
                    easingFunction,
                });
                if (focalPoint.allowedStates && focalPoint.allowedStates.length > 0) {
                    const currentState = this.vortexProduct.getCurrentState();
                    if (!focalPoint.allowedStates.includes(currentState.id)) {
                        yield this.transitionState(focalPoint.allowedStates[0]);
                    }
                }
                yield cameraAction;
            }
            return Promise.resolve();
        });
    }
    /**
     * Finds the position and rotation in 3D space that is associated to the provided page and document coordinates. If the point doesn't appear on the document
     * this function will return undefined.
     */
    getDocumentView({ location, page, debugVisual }) {
        var _a, _b, _c;
        if (this.vortexProduct && this.meshMap) {
            const surfaceMapping = this.vortexProduct.getSurfaceMapping();
            for (const surfaceId of Object.keys(surfaceMapping)) {
                const surface = surfaceMapping[surfaceId];
                const mesh = this.meshMap[surfaceId];
                // The surface contains the target page number
                if (surface.page && surface.page === page) {
                    const { u, v } = documentXYtoUV(location, surface);
                    const { geometry } = mesh;
                    // Only search the geometry if it's a supported geometry
                    if (geometry instanceof BufferGeometry || geometry instanceof Geometry) {
                        const worldMatrix = mesh.matrixWorld;
                        const point = pointNormalFromUV(u, v, mesh.geometry, worldMatrix, this.scene, debugVisual !== null && debugVisual !== void 0 ? debugVisual : false);
                        if (point) {
                            // If a pre-computed focal point already exists for this surface use that instead of the document point normal
                            const focalPoint = ((_a = surface.focalPoint) === null || _a === void 0 ? void 0 : _a.cameraView)
                                ? new Vector3(-surface.focalPoint.cameraView.x, -surface.focalPoint.cameraView.y, -surface.focalPoint.cameraView.z)
                                : point.normal;
                            const spherical = new Spherical().setFromVector3(focalPoint);
                            return {
                                cameraViewFocus: {
                                    point: point.position,
                                    rotation: {
                                        azimuth: spherical.theta,
                                        polar: spherical.phi,
                                    },
                                },
                                allowedStates: (_c = (_b = surface.focalPoint) === null || _b === void 0 ? void 0 : _b.allowedStates) !== null && _c !== void 0 ? _c : [],
                            };
                        }
                    }
                }
            }
        }
        return undefined;
    }
    /**
     * @deprecated The method should not be used, instead call get document view and adjust the camera position, rotation, or focal point
     */
    focusOnDocument(_a) {
        return __awaiter(this, arguments, void 0, function* ({ page, location, polarLock, duration, easingFunction, debugSpheres, }) {
            const view = this.getDocumentView({
                location,
                page,
                debugVisual: debugSpheres,
            });
            if (!view) {
                return;
            }
            yield this.adjustRotation({
                azimuth: view.cameraViewFocus.rotation.azimuth,
                polar: polarLock ? Math.PI / 2 : view.cameraViewFocus.rotation.polar,
                duration: duration !== null && duration !== void 0 ? duration : 2,
                easingFunction,
            });
        });
    }
    /**
     * @deprecated The method should not be used, instead call adjustRotation()
     */
    lookAtAngle({ azimuth, polar, duration, easingFunction, }) {
        return this.camera.lookAt({
            azimuthAngle: azimuth,
            polarAngle: polar !== undefined ? polar : Math.PI / 2,
        }, duration, easingFunction);
    }
    transitionState(stateId, stateOptions) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j;
            if (this.vortexProduct) {
                const currentState = this.vortexProduct.getCurrentState();
                // product already in the given state, no nothing
                if (stateId === currentState.id) {
                    return Promise.resolve();
                }
                const targetState = this.vortexProduct.getStates().find((state) => state.id === stateId);
                // given state is not valid state, throw error
                if (!targetState) {
                    throw new Error(`'${stateId}' not found on product!`);
                }
                if (targetState.dependency || currentState.dependency) {
                    // Determine auto center/zoom options from the state
                    const duration = (_e = (_c = (_a = stateOptions === null || stateOptions === void 0 ? void 0 : stateOptions.duration) !== null && _a !== void 0 ? _a : (_b = targetState.dependency) === null || _b === void 0 ? void 0 : _b.animationDuration) !== null && _c !== void 0 ? _c : (_d = currentState.dependency) === null || _d === void 0 ? void 0 : _d.animationDuration) !== null && _e !== void 0 ? _e : 1;
                    const autoCenter = (_f = stateOptions === null || stateOptions === void 0 ? void 0 : stateOptions.centerGeometry) !== null && _f !== void 0 ? _f : false;
                    const autoZoom = (_g = stateOptions === null || stateOptions === void 0 ? void 0 : stateOptions.scaleToFitGeometry) !== null && _g !== void 0 ? _g : false;
                    if (targetState.boundingBox) {
                        this.boundingBox = buildBoundingBoxGeometry(targetState.boundingBox, this.normalizationFactor);
                        (_h = this.boxHelper) === null || _h === void 0 ? void 0 : _h.setFromObject(this.boundingBox);
                    }
                    else {
                        this.currentGeometry && ((_j = this.boxHelper) === null || _j === void 0 ? void 0 : _j.setFromObject(this.currentGeometry));
                        this.boundingBox = undefined;
                    }
                    // play all animations needed to get from current state to target state
                    yield this.changeStateRecursively(this.vortexProduct, currentState, targetState, duration, autoCenter, autoZoom, stateOptions === null || stateOptions === void 0 ? void 0 : stateOptions.targetOrientation);
                    this.vortexProduct.setCurrentState(stateId);
                    this.eventManager.triggerStateChangeEvent({
                        preview: this,
                        state: targetState,
                    });
                    return Promise.resolve();
                }
                // neither target nor current state has dependency meaning no associated animation, throw error
                throw new Error(`Cannot change to '${stateId}' because '${currentState.id}' has no dependencies!`);
            }
            throw new Error("Cannot change state, no vortex product loaded!");
        });
    }
    performAction(action) {
        return __awaiter(this, void 0, void 0, function* () {
            const promises = [];
            this.animationManager.cleanAnimations();
            // add animation
            if (action.animation !== undefined) {
                promises.push(action.animation.start());
                this.pushAnimation(action.animation);
            }
            // add lookAt
            if (action.cameraOptions && action.cameraOptions.targetOrientation) {
                promises.push(this.adjustRotation({
                    azimuth: action.cameraOptions.targetOrientation.azimuthAngle,
                    polar: action.cameraOptions.targetOrientation.polarAngle,
                    duration: action.cameraOptions.duration,
                    easingFunction: action.cameraOptions.easingFunction,
                }));
            }
            // add focus
            if (action.cameraOptions && action.cameraOptions.geometrySize) {
                const actualSize = {
                    width: action.cameraOptions.geometrySize.width * this.normalizationFactor,
                    height: action.cameraOptions.geometrySize.height * this.normalizationFactor,
                    depth: action.cameraOptions.geometrySize.depth * this.normalizationFactor,
                };
                const targetZoom = this.camera.computeZoom(actualSize);
                promises.push(this.camera.adjustZoomFactor(targetZoom, action.cameraOptions.duration));
            }
            // Perform model actions (i.e. centering)
            if (action.modelOptions && this.currentGeometry) {
                if (action.modelOptions.center) {
                    // Load the bounds from the initial and target states
                    const centerOptions = {
                        duration: action.modelOptions.duration,
                        initialBounds: action.modelOptions.center.initialBounds,
                        targetBounds: action.modelOptions.center.targetBounds,
                    };
                    const centerAnimation = new CenterAnimation(this.currentGeometry, centerOptions);
                    promises.push(centerAnimation.start());
                    this.pushAnimation(centerAnimation);
                }
            }
            yield Promise.all(promises);
        });
    }
    pushAnimation(animation) {
        return this.animationManager.pushAnimation(animation);
    }
    updateAnimations(delta) {
        this.animationManager.update(delta);
    }
    updateBoundingBox() {
        var _a, _b;
        if (this.vortexProduct) {
            const currentState = this.vortexProduct.getCurrentState();
            if (currentState.boundingBox) {
                this.boundingBox = buildBoundingBoxGeometry(currentState.boundingBox, this.normalizationFactor);
                (_a = this.boxHelper) === null || _a === void 0 ? void 0 : _a.setFromObject(this.boundingBox);
            }
            else if (this.currentGeometry) {
                (_b = this.boxHelper) === null || _b === void 0 ? void 0 : _b.setFromObject(this.currentGeometry);
            }
            this.productRequiresUpdate = true;
        }
    }
    /**
     * Use with caution in production environments as this will cause the canvas to freeze for
     * as long as it takes to screenshot
     * @returns DataUri of the canvas
     */
    screenCapture(outputType = "image/png") {
        this.holdRenderForUpdate = true;
        this.renderer.render(this.scene, this.camera.threeJsCamera);
        const resp = this.canvas.toDataURL(outputType);
        this.holdRenderForUpdate = false;
        return resp;
    }
    /**
     * Renders a single frame for a provided delta time. This method is automatically called
     * in a render loop when the start() method is initiated. Use this method in caution in production environments.
     */
    renderFrame(deltaTime) {
        var _a, _b, _c, _d, _e;
        if (this.disposed) {
            console.warn("Render called on a disposed preview!");
            return;
        }
        this.camera.update(deltaTime);
        // used to completely stop rendering new frames
        if (!this.holdRenderForUpdate) {
            // Trigger a camera change event for any listeners
            if (this.camera.requiresUpdate) {
                this.eventManager.triggerCameraChangeEvent({
                    preview: this,
                    cameraView: this.getCameraView(),
                });
            }
            // if nothing has changed in the preview then don't render a new frame and waste compute
            if (this.camera.requiresUpdate || this.canvasRequiresUpdate || this.productRequiresUpdate || this.animationManager.requiresUpdate) {
                // Update animations and ensure they got locked at their final state on the current frame before rendering
                this.updateAnimations(deltaTime);
                // Actually tells the webGlRenderer to re-draw the scene
                this.renderer.render(this.scene, this.camera.threeJsCamera);
                // Update canvas shadows if they are enabled. Disable them for the skybox
                if (((_c = (_b = (_a = this.environment) === null || _a === void 0 ? void 0 : _a.shadowOptions) === null || _b === void 0 ? void 0 : _b.canvasShadows) === null || _c === void 0 ? void 0 : _c.enabled) && !this.skyBox) {
                    updateCanvasShadows(this.canvas, this.environment.shadowOptions.canvasShadows, this.camera);
                }
                // Allow optional updates of the vortex product when rendering
                if (this.vortexProduct && this.vortexProduct.sync) {
                    this.vortexProduct.sync({ preview: this });
                }
                // update bounding box if enabled
                if ((_d = this.webGlOptions) === null || _d === void 0 ? void 0 : _d.renderBoundingBox) {
                    (_e = this.boxHelper) === null || _e === void 0 ? void 0 : _e.update();
                }
                this.productRequiresUpdate = false;
                this.canvasRequiresUpdate = false;
            }
        }
    }
    /**
     * Dispose the product and flush all textures
     */
    dispose() {
        cancelAnimationFrame(this.resizeObserverRafId);
        // Dispose the product and materials
        this.vortexProduct && this.vortexProduct.dispose();
        this.materialManager.dispose();
        this.renderer.dispose();
        this.canvasResizeObserver.disconnect();
        // Remove canvas interactions
        this.eventManager.dispose();
        // Dispose the camera and end the render loop
        this.camera.dispose();
        cancelAnimationFrame(this.rafId);
        this.animationManager.cancelAnimations();
        releaseCanvases([this.canvas]);
        this.disposed = true;
    }
    observeResize() {
        if (this.canvasResizeObserver) {
            this.canvasResizeObserver.disconnect();
        }
        this.canvasResizeObserver.observe(this.canvas);
    }
    // Loads the geometry and normalizes the scale to a specific max dimension
    loadGeometry(vortexProduct) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d;
            const geometry = yield vortexProduct.getGeometry();
            // Build a mapping for each mesh to it's uuid
            this.meshMap = BuildMeshMap(geometry);
            const states = vortexProduct.getStates();
            const initialStateId = vortexProduct.getCurrentState().id;
            // Get the bounds of each state after loading
            const stateBounds = yield getStateBounds(this, vortexProduct, geometry);
            let maximumStateVolume = 0;
            let maximumGeometryBounds = new Vector3();
            let initialGeometryBounds;
            // Find the state with the maximum volume
            for (const state of states) {
                // Load the current state bounds
                const bounds = stateBounds[state.id];
                // Compute the volume of the state bounds
                const stateSize = bounds.getSize(new Vector3());
                const stateVolume = stateSize.x * stateSize.y * stateSize.z;
                // If this state's volume is greater mark it as the maximum bounds
                if (stateVolume > maximumStateVolume) {
                    maximumStateVolume = stateVolume;
                    maximumGeometryBounds = stateSize.clone();
                }
                // use initial state's geometry size as maximumGeometrySize later to scale by inital state.
                // the actual max geometry still needs to be used to accurately calculate the normaized factor
                if (((_a = this.productLoadingOptions) === null || _a === void 0 ? void 0 : _a.scaleToInitialState) && state.id === initialStateId) {
                    initialGeometryBounds = stateSize.clone();
                }
            }
            // Normalize to the maximum geometry size
            const maxDimension = Math.max(maximumGeometryBounds.x, maximumGeometryBounds.y, maximumGeometryBounds.z);
            const normalizationFactor = MAX_GEOMETRY_DIMENSION / maxDimension;
            this.normalizationFactor = normalizationFactor;
            // Scale and position the geometry based on the normalized factor
            geometry.scale.multiplyScalar(normalizationFactor);
            geometry.position.multiplyScalar(normalizationFactor);
            // Compute the maximum geometry size (this can be used later if the canvas size changes)
            // use initial geometry size when scaleToInitialState flag is set (initialGeometryBounds
            // is only defined if that flag is set to true)
            this.maximumGeometrySize = {
                width: ((_b = initialGeometryBounds === null || initialGeometryBounds === void 0 ? void 0 : initialGeometryBounds.x) !== null && _b !== void 0 ? _b : maximumGeometryBounds.x) * normalizationFactor,
                height: ((_c = initialGeometryBounds === null || initialGeometryBounds === void 0 ? void 0 : initialGeometryBounds.y) !== null && _c !== void 0 ? _c : maximumGeometryBounds.y) * normalizationFactor,
                depth: ((_d = initialGeometryBounds === null || initialGeometryBounds === void 0 ? void 0 : initialGeometryBounds.z) !== null && _d !== void 0 ? _d : maximumGeometryBounds.z) * normalizationFactor,
            };
            // Set the camera to the new orbit radius based on size
            this.camera.initializeCamera(this.maximumGeometrySize);
            return geometry;
        });
    }
    createActionFromState(product, current, target, duration, reverse, autoCenter, autoZoom, targetOrientation) {
        const action = { id: target.id };
        if (target.dependency) {
            // eslint-disable-next-line operator-linebreak
            const reverseDirection = target.dependency.animationDirection === AnimationDirection.forward ? AnimationDirection.backward : AnimationDirection.forward;
            const animationOptions = {
                animationId: target.dependency.animationId,
                direction: reverse ? reverseDirection : target.dependency.animationDirection,
                duration,
            };
            action.animation = product.buildAnimation(animationOptions);
            // Auto-center the model if required. Take into account animation direction for determining
            // the target and initial state locations
            if (autoCenter && this.stateBounds[current.id] && this.stateBounds[target.id]) {
                action.modelOptions = {
                    duration,
                    center: {
                        initialBounds: reverse ? this.stateBounds[target.id] : this.stateBounds[current.id],
                        targetBounds: reverse ? this.stateBounds[current.id] : this.stateBounds[target.id],
                    },
                };
            }
            // Auto-center the model if required. Take into account animation direction for determining
            // the target and initial state locations
            if (autoZoom && this.stateBounds[current.id] && this.stateBounds[target.id]) {
                const bounds = reverse ? this.stateBounds[current.id] : this.stateBounds[target.id];
                const boundSize = bounds.getSize(new Vector3());
                // Ensure the state bounds are re-normalized to max geometry dimension (200) for zooming in/out
                action.cameraOptions = {
                    duration,
                    geometrySize: {
                        width: boundSize.x / this.normalizationFactor,
                        height: boundSize.y / this.normalizationFactor,
                        depth: boundSize.z / this.normalizationFactor,
                    },
                };
            }
        }
        // Add an optional target orientation for the animation
        if (targetOrientation) {
            if (action.cameraOptions) {
                action.cameraOptions.targetOrientation = targetOrientation;
            }
            else {
                action.cameraOptions = {
                    duration,
                    targetOrientation,
                };
            }
        }
        return action;
    }
    changeStateRecursively(product, current, target, duration, autoCenter, autoZoom, targetOrientation) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            // end condition: one animation get model to target state
            if (target.dependency && target.dependency.stateId === current.id) {
                // play target state animation
                yield this.performAction(this.createActionFromState(product, current, target, duration, false, autoCenter, autoZoom, targetOrientation));
                return target;
            }
            if (current.dependency && current.dependency.stateId === target.id) {
                // play current state animation in reverse
                yield this.performAction(this.createActionFromState(product, target, current, duration, true, autoCenter, autoZoom, targetOrientation));
                return target;
            }
            // find second to the last target state
            const states = product.getStates();
            let newTarget = states.find((state) => { var _a; return state.id === ((_a = target.dependency) === null || _a === void 0 ? void 0 : _a.stateId); });
            // TODO: It seems we can improve this one with a recursive function maybe?
            if (!newTarget || ((_a = newTarget.dependency) === null || _a === void 0 ? void 0 : _a.stateId) !== target.id) {
                let dependencyState = current;
                // eslint-disable-next-line no-constant-condition
                while (true) {
                    // eslint-disable-next-line no-loop-func
                    const middleState = states.find((state) => { var _a; return state.id === ((_a = dependencyState.dependency) === null || _a === void 0 ? void 0 : _a.stateId); });
                    if (middleState) {
                        if (((_b = middleState.dependency) === null || _b === void 0 ? void 0 : _b.stateId) === target.id) {
                            newTarget = middleState;
                            break;
                        }
                        dependencyState = middleState;
                    }
                    else {
                        break;
                    }
                }
            }
            if (!newTarget) {
                throw new Error("Cannot find state stated in dependency");
            }
            // call recursively with new target state to get new current state
            const newCurrent = yield this.changeStateRecursively(product, current, newTarget, duration, autoCenter, autoZoom, targetOrientation);
            // play animation to reach target state
            if (target.dependency && target.dependency.stateId === newCurrent.id) {
                // play target state animation
                yield this.performAction(this.createActionFromState(product, current, target, duration, false, autoCenter, autoZoom, targetOrientation));
                return target;
            }
            if (newCurrent.dependency && newCurrent.dependency.stateId === target.id) {
                // play current state animation in reverse
                yield this.performAction(this.createActionFromState(product, current, newCurrent, duration, true, autoCenter, autoZoom, targetOrientation));
                return target;
            }
            throw new Error("Did not play animation to move on to next state");
        });
    }
}
//# sourceMappingURL=preview.js.map